import type { Individual, ShownStatement } from "../../../../knowledgeData";
import { parseNTriple, RDF_TYPE, type RdfTriple } from "../../../../../shared/rdf/nTriples";
import type { PlacedStatement } from "../regestParts";

/** A group of entities as the project's view profile names it (C-B7 AB-5): the classes it gathers, in order. */
export interface EntityClasses {
  readonly classes: readonly string[];
}

/** The words of one statement that names an entity and says nothing else, in this text. */
export interface EntityMention {
  /** The statement's key, which its words in the text carry. */
  readonly key: string;
  readonly words: string;
  /** The part of the text it stands in (`regestParts`); `null` for a text without parts. */
  readonly segmentPath: string | null;
  readonly start: number;
}

/** One person, place, benefice or other entity, with what touches it across the document's statements. */
export interface EntityCard {
  readonly individual: Individual;
  /** Its label, else the words it is first named by; `null` for neither. */
  readonly name: string | null;
  /** Its words in this text, in reading order. */
  readonly mentions: readonly EntityMention[];
  /** The lines (`shown.key`) that say something about it, in the order given. */
  readonly statements: readonly string[];
}

/** A profile group with its cards; `shape` is `null` for the entities of no group (Daniel's "Other"). */
export interface EntityGroup<G extends EntityClasses> {
  readonly shape: G | null;
  readonly cards: readonly EntityCard[];
}

export interface EntitySource {
  /** `<document>@<version>`: only spans in this text are its words. */
  readonly document: string;
  /** Every line shown, conclusions included, in the order their numbers follow. */
  readonly lines: readonly ShownStatement<PlacedStatement>[];
  /** The individuals the statements describe (`groupIndividuals`), in the order they first appear. */
  readonly individuals: readonly Individual[];
  /** Whether an individual belongs to the evidence layer (the profile's evidence classes). */
  readonly isEvidence: (individual: Individual) => boolean;
  /** Every class above one of `classes` by the data model's parents. */
  readonly classesAbove: (classes: readonly string[]) => ReadonlySet<string>;
  /** The properties by which an evidence individual stands for one entity (the profile's `entity_links`). */
  readonly links: ReadonlySet<string>;
  /** The part each statement sits in, by its key (`regestParts`). */
  readonly partOf: ReadonlyMap<string, string>;
}

/**
 * The entities of a document and what touches each (Daniel's regest viewer, `render_entities`): a
 * card for each individual of a profile group's class, and for each other individual a statement
 * introduces by its class and words alone, as his attested entities are, under "Other". A group
 * takes an individual of one of its classes before one of a class above it; groups keep the
 * profile's order. A statement touches an entity when its triples name it, or name what stands for
 * it by one of the profile's entity links (a mention's attestation, a claim's evidence set), as
 * Daniel's paths do; never through the source text the statements are anchored in, which may name
 * other things, such as its archive. A statement that says nothing else gives the entity its words,
 * each passage once; one that says something is listed as a line. No groups, no cards.
 */
export function entityGroups<G extends EntityClasses>(source: EntitySource, groups: readonly G[]): EntityGroup<G>[] {
  if (groups.length === 0) return [];
  // Every statement once, with the line that shows it; one absorbed into a line names only what that line shows.
  const statements = source.lines.flatMap((line) => [
    { placed: line.shown, line: line.shown.key, says: line.layout.said.some((description) => description.rows.length > 0) },
    ...line.absorbed.map((placed) => ({ placed, line: line.shown.key, says: false })),
  ]).map((statement) => ({ ...statement, triples: statement.placed.statement.triples.flatMap((line) => parseNTriple(line) ?? []) }));

  const introduced = new Set(statements.filter((statement) => !statement.says).flatMap((statement) => statement.triples
    .flatMap((triple) => (triple.predicate === RDF_TYPE && triple.subject.kind === "iri" ? [`<${triple.subject.iri}>`] : []))));
  const groupOf = new Map<string, G | null>();
  for (const individual of source.individuals) {
    if (source.isEvidence(individual) || individual.classes.length === 0) continue;
    const above = source.classesAbove(individual.classes);
    const group = groups.find((shape) => shape.classes.some((iri) => individual.classes.includes(iri)))
      ?? groups.find((shape) => shape.classes.some((iri) => above.has(iri)));
    if (group !== undefined) groupOf.set(individual.iri, group);
    else if (introduced.has(individual.iri)) groupOf.set(individual.iri, null);
  }

  // What stands for which entity, by the profile's links (an attestation for its entity), wherever it is said.
  const standsFor = new Map<string, Set<string>>();
  for (const { triples } of statements) {
    for (const triple of triples) {
      if (!source.links.has(triple.predicate) || triple.subject.kind !== "iri" || triple.object.kind !== "iri") continue;
      const subject = `<${triple.subject.iri}>`;
      standsFor.set(subject, new Set([...(standsFor.get(subject) ?? []), `<${triple.object.iri}>`]));
    }
  }

  const mentions = new Map<string, EntityMention[]>();
  const lines = new Map<string, string[]>();
  for (const statement of statements) {
    const named = new Set(statement.triples.flatMap(namedIris));
    const touched = new Set([...named].flatMap((iri) => [iri, ...(standsFor.get(iri) ?? [])]).filter((iri) => groupOf.has(iri)));
    for (const entity of touched) {
      if (statement.says) {
        const listed = lines.get(entity) ?? [];
        if (!listed.includes(statement.line)) lines.set(entity, [...listed, statement.line]);
        continue;
      }
      const spans = statement.placed.statement.evidence.filter((span) => span.segment.split("#")[0] === source.document);
      if (spans.length === 0) continue;
      // `regestParts` places everything in a text without parts under the path "": no part to name.
      const part = source.partOf.get(statement.placed.key);
      const mention = {
        key: statement.placed.key,
        words: spans.map((span) => span.quote).join(" … "),
        segmentPath: part === undefined || part === "" ? null : part,
        start: Math.min(...spans.map((span) => span.start)),
      };
      const known = mentions.get(entity) ?? [];
      if (known.some((other) => other.key === mention.key || (other.start === mention.start && other.words === mention.words))) continue;
      mentions.set(entity, [...known, mention]);
    }
  }

  const cardOf = (individual: Individual): EntityCard => {
    const words = [...(mentions.get(individual.iri) ?? [])].sort((a, b) => a.start - b.start);
    return { individual, name: individual.name ?? words[0]?.words ?? individual.words, mentions: words, statements: lines.get(individual.iri) ?? [] };
  };
  const inGroup = (shape: G | null) => source.individuals.filter((individual) => groupOf.has(individual.iri) && groupOf.get(individual.iri) === shape).map(cardOf);
  return [...groups, null]
    .map((shape) => ({ shape, cards: inGroup(shape) }))
    .filter((group) => group.cards.length > 0);
}

/** The individuals a triple names, by IRI as N-Triples writes them; not the classes it gives. */
function namedIris(triple: RdfTriple): string[] {
  const subject = triple.subject.kind === "iri" ? [`<${triple.subject.iri}>`] : [];
  const object = triple.object.kind === "iri" && triple.predicate !== RDF_TYPE ? [`<${triple.object.iri}>`] : [];
  return [...subject, ...object];
}

